import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ErrorMessage, FeatureFlag, Route } from 'config';
import { getQueryParam } from 'utils/location';
import { runsWebview } from 'utils/device';
import { closeWebview } from 'utils/webview';
import useCurrentUser from 'hooks/useCurrentUser';

import { Component as LeftArrowIcon } from 'consumer/pages/recipes/left-arrow-on.svg';

import PauseSubscription from './PauseSubscription';
import ReactivateSubscription from './ReactivateSubscription';
import CancelSubscription from './CancelSubscription';

function ManageSubscription(): ReactElement {
  const actionQueryParam = getQueryParam('action');
  const { currentUser } = useCurrentUser();
  const navigate = useNavigate();
  const isAccountManagementEnabled = currentUser.hasFeatureEnabled(FeatureFlag.ACCOUNT_MANAGEMENT);

  const [errors, setErrors] = useState(false);
  const {
    subscriptionStatus,
    isReturning,
    isSuspended,
    isCancelled,
  } = currentUser;

  const handleErrors = () => {
    setErrors(true);
  };

  const handleBackToProfile = () => {
    if (runsWebview) {
      closeWebview();
    } else {
      navigate(Route.PROFILE);
    }
  };

  useEffect(() => {
    document.body.classList.add('u-background--gray');

    return () => {
      document.body.classList.remove('u-background--gray');
    };
  }, []);

  let subscriptionManagementComponent;
  if (actionQueryParam === 'cancel') {
    subscriptionManagementComponent = (<CancelSubscription handleErrors={handleErrors} />);
  } else if (isReturning || (isCancelled && isAccountManagementEnabled)) {
    subscriptionManagementComponent = (<ReactivateSubscription handleErrors={handleErrors} />);
  } else {
    subscriptionManagementComponent = (<PauseSubscription handleErrors={handleErrors} />);
  }

  return (
    <div id="subscription-management">
      <div className="l-container js-subscription-management-panel" style={{ height: '100%' }}>
        <div className="c-subscription-management-header">
          <Link
            to={Route.PROFILE}
            className={runsWebview ? 'd-none' : ''}
            data-cy="manage-subscription-back-to-profile"
          >
            <LeftArrowIcon height="18" width="32" />
            <span>
              Back to My Account
            </span>
          </Link>
          <hr />
          <hr />

          {(isSuspended || (isCancelled && !isAccountManagementEnabled)) && (
            <p className="u-text u-text--lg u-text--error">
              Your account is&nbsp;
              <span>
                {subscriptionStatus}
              </span>
            </p>
          )}

          {(errors) && (
            <p className="u-text u-text--lg u-text--error">
              {ErrorMessage.CHANGES_NOT_SAVED}
            </p>
          )}
        </div>

        {subscriptionManagementComponent}
      </div>

      {actionQueryParam === 'cancel' && (
        <div className="c-subscription-management-cancel-group">
          <div className="c-subscription-management-footer__profile_link">
            <button
              type="button"
              className="c-button c-button--primary"
              aria-label="Back To My Account"
              onClick={handleBackToProfile}
            >
              Back To My Account
            </button>
          </div>

          <div
            className={[
              'c-subscription-management-footer',
              runsWebview ? 'd-none' : '',
            ].join(' ')}
          >
            <hr />
            <h4 className="c-heading c-heading--brand-7">
              Have any questions?
            </h4>
            <p className="u-text u-text--lg">
              Check out our&nbsp;
              <a
                href="https://purplecarrot.zendesk.com/hc/en-us"
                data-cy="manage-subscription-faq"
              >
                <abbr>FAQs</abbr>
              </a>
              &nbsp;page or&nbsp;
              <a
                href="/contacts/new"
                data-cy="manage-subscription-contact-us"
              >
                contact us
              </a>

            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageSubscription;
